import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import TableSortLabel from '@mui/material/TableSortLabel';
import ReactCountryFlag from 'react-country-flag';
import countryCodeMapping from '../../utils/CodeCountryMapping.json';
import Button from '@mui/material/Button';

// === GUTTER UNIFICADO PARA TODAS LAS VISTAS ===
const LEFT_GUTTER = '15px';

// === CONFIG DE GRID SOLO PARA 1200–1387 ===
const GRID_COLS_INTERMEDIATE = '80px 1fr 100px 120px 120px';
// Pos | Name | Country | Region | Total Score

const StyledTableHeadRow = styled(TableRow)(({ isIntermediateView }) => ({
    backgroundColor: '#4A4A4A',
    border: '1px solid white',
    borderRadius: '10px',
    marginBottom: '5px',
    height: '50px',
    alignItems: 'center',
    color: 'white',
    paddingLeft: LEFT_GUTTER,
    display: isIntermediateView ? 'grid' : 'flex',
    gridTemplateColumns: isIntermediateView ? GRID_COLS_INTERMEDIATE : undefined,
}));

const StyledTableRow = styled(TableRow)(({ isIntermediateView }) => ({
    backgroundColor: '#222222',
    border: '1px solid white',
    borderRadius: '10px',
    marginBottom: '5px',
    height: '65px',
    alignItems: 'center',
    color: 'white',
    position: 'relative',
    paddingLeft: LEFT_GUTTER,
    paddingRight: 0,
    display: isIntermediateView ? 'grid' : 'flex',
    gridTemplateColumns: isIntermediateView ? GRID_COLS_INTERMEDIATE : undefined,
}));

const LeftAlignTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
    fontWeight: 'bold',
    border: 'none',
    fontSize: '14px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: 'white',
    ...(isIntermediateView
        ? { width: 'auto', flex: 'unset' }
        : { width: isMobileView ? 'auto' : '190px', flex: isMobileView ? '1 1 auto' : '0 0 190px' }),
    overflow: 'hidden',
}));

const CenterAlignTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
    fontWeight: 'bold',
    border: 'none',
    fontSize: '14px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    ...(isIntermediateView ? { width: 'auto' } : { width: isMobileView ? '70px' : '160px' }),
}));

const NarrowTableCell = styled(TableCell)(({ isMobileView, isIntermediateView }) => ({
    fontWeight: 'bold',
    border: 'none',
    fontSize: '14px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    ...(isIntermediateView ? { width: 'auto' } : { width: isMobileView ? '60px' : '100px' }),
}));

const StyledTableSortLabel = styled(TableSortLabel)(() => ({
    color: 'white',
    '&.Mui-active': { color: 'white' },
    '& .MuiTableSortLabel-icon': {
        color: 'white !important',
        marginLeft: '4px',
    },
    '@media (min-width:1200px) and (max-width:1387px)': {
        '& .MuiTableSortLabel-icon': { marginLeft: '2px' },
    },
}));

const OutsideTableCell = styled(TableCell)(({ isMediumScreen }) => ({
    position: 'absolute',
    right: '5%',
    top: '50%',
    transform: 'translateY(-50%)',
    fontWeight: 'bold',
    border: 'none',
    fontSize: '14px',
    display: 'flex-end',
    color: 'white',
    width: '100px',
}));

const CompaniesTable = ({ firms, sortBy, setSortBy }) => {
    const navigate = useNavigate();

    const isSmallScreen = useMediaQuery('(max-width:980px)');
    const isIntermediateView = useMediaQuery('(min-width:1200px) and (max-width:1400px)');
    const isMobileView = isSmallScreen;

    const isMediumScreen = useMediaQuery('(max-width:1200px)');
    const isSpecificScreen = useMediaQuery('(max-width:1250px)');
    const isMobileOrNext = useMediaQuery('(max-width:900px)');

    const handleSort = (field) => {
        setSortBy({
            field,
            direction: sortBy.direction === 'asc' ? 'desc' : 'asc',
        });
    };
    const handleClick = (firm) => {
        const companyId = firm?.id;
        const companySlug = firm?.slug ?? String(companyId ?? '');
        const countrySlug = firm?.countrySlug ?? 'unknown-country';
        if (companyId != null) {
            localStorage.setItem('companyId', companyId);
        }
        navigate(`/market-company/${countrySlug}/${companySlug}`);
    };

    return (
        <TableContainer
            component={Paper}
            style={{
                border: 'none',
                boxShadow: 'none',
                backgroundColor: '#222222',
                overflowX: isMediumScreen ? 'auto' : 'hidden',
                overflowY: 'hidden',
                width: '100%',
            }}
        >
            <Table style={{ width: '100%' }}>
                <TableHead>
                    <StyledTableHeadRow isIntermediateView={isIntermediateView}>
                        <NarrowTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                            <StyledTableSortLabel
                                active={sortBy.field === 'position'}
                                direction={sortBy.field === 'position' ? sortBy.direction : 'asc'}
                                onClick={() => handleSort('position')}
                            >
                                {isMobileView ? 'Pos' : 'Position'}
                            </StyledTableSortLabel>
                        </NarrowTableCell>

                        <LeftAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                            <StyledTableSortLabel
                                active={sortBy.field === 'institutionName'}
                                direction={sortBy.field === 'institutionName' ? sortBy.direction : 'asc'}
                                onClick={() => handleSort('institutionName')}
                            >
                                Name
                            </StyledTableSortLabel>
                        </LeftAlignTableCell>

                        <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                            <StyledTableSortLabel
                                active={sortBy.field === 'country'}
                                direction={sortBy.field === 'country' ? sortBy.direction : 'asc'}
                                onClick={() => handleSort('country')}
                            >
                                Country
                            </StyledTableSortLabel>
                        </CenterAlignTableCell>

                        {(!isSmallScreen || isIntermediateView) && (
                            <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                <StyledTableSortLabel
                                    active={sortBy.field === 'region'}
                                    direction={sortBy.field === 'region' ? sortBy.direction : 'asc'}
                                    onClick={() => handleSort('region')}
                                >
                                    Region
                                </StyledTableSortLabel>
                            </CenterAlignTableCell>
                        )}

                        <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                            <StyledTableSortLabel
                                active={sortBy.field === 'totalScore'}
                                direction={sortBy.field === 'totalScore' ? sortBy.direction : 'asc'}
                                onClick={() => handleSort('totalScore')}
                            >
                                Total Score
                            </StyledTableSortLabel>
                        </CenterAlignTableCell>
                    </StyledTableHeadRow>
                </TableHead>
                <TableBody>
                    {firms.map((firm) => {
                        const MAX_NAME_LENGTH = 26;
                        const shouldTruncate =
                            (isMobileOrNext || isIntermediateView) &&
                            firm.institutionName.length > MAX_NAME_LENGTH;
                        const firmName = shouldTruncate
                            ? firm.institutionName.substring(0, MAX_NAME_LENGTH) + '…'
                            : firm.institutionName;

                        return (
                            <StyledTableRow key={firm.id} isIntermediateView={isIntermediateView}>
                                <NarrowTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                    {firm.position}
                                </NarrowTableCell>
                                <LeftAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                    {(isMobileView || isIntermediateView) ? (
                                        <div
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '6px',
                                            }}
                                            onClick={() => handleClick(firm)}
                                        >
                                            <div
                                                style={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'pointer',
                                                }}
                                                title={firm.institutionName}
                                            >
                                                {firmName}
                                            </div>
                                            <Button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleClick(firm);
                                                }}
                                                style={{
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    whiteSpace: 'nowrap',
                                                    textDecoration: 'underline',
                                                    textDecorationThickness: '2px',
                                                    padding: 0,
                                                    minWidth: 0,
                                                    alignSelf: 'flex-start',
                                                }}
                                            >
                                                Profile
                                            </Button>
                                        </div>
                                    ) : (
                                        firm.institutionName
                                    )}
                                </LeftAlignTableCell>

                                <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                    <div
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <ReactCountryFlag
                                            countryCode={countryCodeMapping[firm.country.id] || 'US'}
                                            svg
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                            title={firm.country.name}
                                        />
                                    </div>
                                </CenterAlignTableCell>

                                {(!isSmallScreen || isIntermediateView) && (
                                    <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                        {firm.country.region}
                                    </CenterAlignTableCell>
                                )}

                                <CenterAlignTableCell isMobileView={isMobileView} isIntermediateView={isIntermediateView}>
                                    {firm.totalScore ? firm.totalScore.toFixed(2) : 'N/A'}
                                </CenterAlignTableCell>

                                {(!isMobileView && !isIntermediateView) && (
                                    <OutsideTableCell isMediumScreen={isMediumScreen}>
                                        <Button
                                            onClick={() => handleClick(firm)}
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: 'white',
                                                whiteSpace: 'nowrap',
                                                textDecoration: 'underline',
                                                textDecorationThickness: '2px',
                                                marginLeft: (isSpecificScreen || isMediumScreen) ? '50px' : '15px',
                                            }}
                                        >
                                            {(isSpecificScreen || isMediumScreen) ? 'Profile' : 'Company Profile'}
                                        </Button>
                                    </OutsideTableCell>
                                )}
                            </StyledTableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CompaniesTable;
